import { useMutation, useQueryClient } from '@tanstack/react-query';

import { replaceTaskLabels as replaceTaskLabelsRequest } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import type {
  TaskLabelView,
  WorkspaceAggregate,
} from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';

export interface ReplaceTaskLabelsInput {
  workspaceId: string;
  taskId: string;
  /** The labels the task should carry afterwards — the whole set, not a delta. */
  labelIds: string[];
  /** What the task's chips should look like while the write is in flight. */
  optimistic: readonly TaskLabelView[];
}

interface MutationContext {
  previousAggregate: WorkspaceAggregate | null | undefined;
}

function withLabels(
  aggregate: WorkspaceAggregate | null | undefined,
  taskId: string,
  labels: readonly TaskLabelView[],
): WorkspaceAggregate | null | undefined {
  if (!aggregate) return aggregate;
  return {
    ...aggregate,
    tasks: aggregate.tasks.map((task) =>
      task.id === taskId ? { ...task, labels: [...labels] } : task,
    ),
  };
}

/**
 * Replace the whole set of labels on a task.
 *
 * Follows `useMoveTask` rather than inventing a second cache pattern: patch
 * optimistically, **merge the server's answer on success**, and invalidate only
 * when the write fails. Both are the same kind of write — frequent, interactive,
 * per-item, performed while the board polls at two seconds behind it — and
 * invalidating on the happy path would re-GET every task, workflow and label in
 * the workspace on each chip toggle, on top of the optimistic update that had
 * already made the UI correct.
 *
 * **Sends no `expected_version`.** Labelling is not a move: the version guards
 * *placement*, and the backend leaves it untouched here precisely so a drag begun
 * before a label was attached still succeeds.
 */
export function useReplaceTaskLabels() {
  const queryClient = useQueryClient();

  return useMutation<
    TaskLabelView[],
    Error,
    ReplaceTaskLabelsInput,
    MutationContext
  >({
    mutationFn: async ({ workspaceId, taskId, labelIds }) =>
      expectStatus(
        await replaceTaskLabelsRequest(workspaceId, taskId, {
          label_ids: labelIds,
        }),
        200,
        'The labels could not be saved.',
      ).labels,

    onMutate: async ({ workspaceId, taskId, optimistic }) => {
      await queryClient.cancelQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
      const previousAggregate = queryClient.getQueryData<WorkspaceAggregate>(
        workspaceKeys.detail(workspaceId),
      );
      queryClient.setQueryData(
        workspaceKeys.detail(workspaceId),
        (prev: WorkspaceAggregate | null | undefined) =>
          withLabels(prev, taskId, optimistic),
      );
      return { previousAggregate };
    },

    onSuccess: (labels, { workspaceId, taskId }) => {
      // The server's answer, not the optimistic guess: a label created in the
      // same gesture arrives here with its assigned colour slot, which the
      // optimistic patch could only have invented.
      queryClient.setQueryData(
        workspaceKeys.detail(workspaceId),
        (prev: WorkspaceAggregate | null | undefined) =>
          withLabels(prev, taskId, labels),
      );
    },

    onError: (_error, { workspaceId }, context) => {
      if (context) {
        queryClient.setQueryData(
          workspaceKeys.detail(workspaceId),
          context.previousAggregate,
        );
      }
      // Only here. A failed write is the one case where what the cache holds is
      // genuinely unknown.
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
    },
  });
}
